import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { Payment, Order } from '../../../api/models';
import { OrderService } from '../../../api/services/order.service';
import { OrderDetailDialogComponent } from '../../staff-dashboard/order-detail-dialog/order-detail-dialog.component';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-payment-detail-dialog',
  standalone: true,
  imports: [
    CommonModule, 
    MatDialogModule, 
    MatButtonModule, 
    MatIconModule,
    MatChipsModule,
    MatDividerModule
  ],
  template: `
    <h2 mat-dialog-title class="flex items-center justify-between">
      <span>Payment Details</span>
      <span class="text-sm font-normal text-gray-500">{{ payment.transactionId || payment.id }}</span>
    </h2>
    <mat-dialog-content>
      <div class="space-y-4 py-2">
        
        <div class="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
          <span class="text-gray-600">Status</span>
          <mat-chip [color]="getStatusColor(payment.status)" selected>
            {{ payment.status | titlecase }}
          </mat-chip>
        </div>

        <!-- Refund Reason Section -->
        <div *ngIf="payment.status === 'REFUNDED' && payment.refundReason" class="bg-red-50 border border-red-100 p-3 rounded-lg">
          <div class="flex items-start gap-2 text-red-700">
            <mat-icon class="text-red-500 text-sm mt-0.5" style="width: 18px; height: 18px; font-size: 18px;">info</mat-icon>
            <div>
              <div class="text-xs font-semibold uppercase opacity-75">Refund Reason</div>
              <div class="text-sm">{{ payment.refundReason }}</div>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-xs text-gray-500 uppercase">Amount</label>
            <div class="text-xl font-bold">\${{ payment.amount.toFixed(2) }}</div>
          </div>
          <div *ngIf="payment.refundAmount">
            <label class="block text-xs text-red-500 uppercase">Refund Amount</label>
            <div class="text-xl font-bold text-red-500">\${{ payment.refundAmount.toFixed(2) }}</div>
          </div>
          <!-- Tip removed as not in model -->
        </div>

        <mat-divider></mat-divider>

        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <label class="block text-xs text-gray-500 uppercase mb-1">Time</label>
            <div>{{ payment.createdAt | date:'medium' }}</div>
          </div>
          <div>
            <label class="block text-xs text-gray-500 uppercase mb-1">Method</label>
            <div class="flex items-center gap-2">
              <mat-icon class="text-gray-600 text-sm h-4 w-4">{{ getMethodIcon(payment.paymentMethod) }}</mat-icon>
              {{ payment.paymentMethod | titlecase }}
            </div>
          </div>
          <div>
            <label class="block text-xs text-gray-500 uppercase mb-1">Order ID</label>
            <div>{{ payment.orderId || 'N/A' }}</div>
          </div>
           <!-- Customer Name removed as not in model -->
        </div>

      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button color="primary" (click)="viewOrderDetails()" *ngIf="payment.orderId">
        More Detail
      </button>
      <span class="flex-1"></span>
      <button mat-button mat-dialog-close>Close</button>
      <button mat-stroked-button color="primary" [mat-dialog-close]="'receipt'">
        <mat-icon>receipt</mat-icon> Print Receipt
      </button>
    </mat-dialog-actions>
  `
})
export class PaymentDetailDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<PaymentDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public payment: Payment,
    private dialog: MatDialog,
    private orderService: OrderService
  ) {}

  getStatusColor(status: string): string {
    switch(status?.toUpperCase()) {
      case 'COMPLETED': return 'primary';
      case 'PENDING': return 'warn';
      case 'PROCESSING': return 'accent';
      case 'FAILED': return 'warn';
      case 'REFUNDED': return 'primary'; // Changed to warn for visibility? Or keep primary as per original code. Keeping primary for now.
      default: return 'primary';
    }
  }

  getMethodIcon(method: string): string {
    switch(method?.toLowerCase()) {
      case 'card': return 'credit_card';
      case 'cash': return 'payments';
      case 'digital': return 'phone_android';
      default: return 'payment';
    }
  }

  viewOrderDetails(): void {
    if (this.payment.orderId) {
      this.orderService.getOrder(this.payment.orderId).subscribe({
        next: (order: Order) => {
          this.dialog.open(OrderDetailDialogComponent, {
            data: { order },
            width: '600px',
            maxWidth: '95vw'
          });
        },
        error: (err: any) => {
          if (!environment.production) {
            console.error('Error fetching order details:', err);
          }
          // Could add a snackbar notification here if needed
        }
      });
    }
  }
}
